import { useCallback, useEffect, useState } from 'react'
import {
  getScanListForOrderIdAsJson,
  isAndroidBridgeAvailable,
  isZebraDevice,
  startScanning,
  stopScanning,
} from '@/shared/lib/native'
import { SCAN_ALERT_MESSAGES } from './order.zebraScan'
import { showScanAlert } from './showScanAlert'

const START_SUCCESS_CALLBACK = 'onScanStartSuccess'
const START_FAILURE_CALLBACK = 'onScanStartFailure'
const STOP_SUCCESS_CALLBACK = 'onScanStopSuccess'
const STOP_FAILURE_CALLBACK = 'onScanStopFailure'

export interface UseZebraScannerOptions {
  onScanData?: (data: string) => void
}

export interface ZebraScannedCrate {
  orderId: string
  areaCode: string
  crateNumber: string
}

export interface ZebraScanResponse {
  success: boolean
  orderDetail: ZebraScannedCrate | null
  errorText: string | null
}

/** Order id for the Hello session that should accept scans (order details or last dashboard Scan). */
let zebraScanActiveOrderId: string | null = null

export function setZebraScanActiveOrderId(orderId: string | null) {
  zebraScanActiveOrderId = orderId?.trim() || null
}

export function shouldRejectWrongOrderScan(
  parsed: ZebraScanResponse | null,
  activeOrderId: string | null,
): boolean {
  if (!parsed?.success || !activeOrderId) {
    return false
  }
  const scannedId = parsed.orderDetail?.orderId?.trim()
  if (!scannedId) {
    return false
  }
  // The scanner reports the licence plate's first segment, which carries the A prefix.
  return scannedId !== `A${activeOrderId.trim()}`
}

type ScanDataSubscriber = (data: string) => void
type ScanErrorSubscriber = (error: string | null) => void

const scanDataSubscribers = new Set<ScanDataSubscriber>()
const scanErrorSubscribers = new Set<ScanErrorSubscriber>()
let callbackRegistrationCount = 0

function notifyScanData(data: string) {
  scanDataSubscribers.forEach(subscriber => subscriber(data))
}

function notifyScanError(error: string | null) {
  scanErrorSubscribers.forEach(subscriber => subscriber(error))
}

function mapDataWedgeError(error: string): string {
  const map: Record<string, string> = {
    DATAWEDGE_DISABLED: 'Scanner unavailable: DataWedge is disabled on device',
    INPUT_NOT_ENABLED: 'Scanner input is not enabled in DataWedge profile',
    PLUGIN_DISABLED_IN_CONFIG: 'Scanner plugin is disabled in DataWedge configuration',
    SCANNER_ENABLE_FAILED: 'Scanner failed to enable',
    SCANNER_DISABLE_FAILED: 'Scanner failed to disable',
    PARAMETER_INVALID: 'Scanner command failed due to invalid parameters',
    PROFILE_NOT_FOUND: 'Scanner profile not found in DataWedge',
    COMMAND_PENDING: 'Previous scanner command is still in progress',
    UNKNOWN_ERROR: 'Unknown scanner error from device',
  }
  return map[error] ?? error
}

export function parseScanResponse(raw: string): ZebraScanResponse | null {
  try {
    const parsed = JSON.parse(raw) as Partial<ZebraScanResponse>
    if (typeof parsed.success !== 'boolean') {
      return null
    }
    return {
      success: parsed.success,
      orderDetail: parsed.orderDetail ?? null,
      errorText: parsed.errorText ?? null,
    }
  } catch {
    return null
  }
}

function ensureGlobalZebraCallbacks() {
  if (typeof window === 'undefined') {
    return
  }

  window.onScanStartSuccess = () => {
    notifyScanError(null)
  }
  window.onScanStartFailure = (error) => {
    const raw = typeof error === 'string' ? error : JSON.stringify(error)
    notifyScanError(mapDataWedgeError(raw))
  }
  window.onScanStopSuccess = () => {
    notifyScanError(null)
  }
  window.onScanStopFailure = (error) => {
    const raw = typeof error === 'string' ? error : JSON.stringify(error)
    notifyScanError(mapDataWedgeError(raw))
  }
  window.receiveScanData = (data: string) => {
    const parsed = parseScanResponse(data)
    const isSuccess = parsed?.success === true

    if (shouldRejectWrongOrderScan(parsed, zebraScanActiveOrderId)) {
      showScanAlert({ message: SCAN_ALERT_MESSAGES['wrong-order'] })
      return
    }

    if (!isSuccess) {
      // BD-8393 AC2: the native layer's own error text is not ours to show - it can
      // carry barcode strings and internal field names, which mean nothing on the
      // floor. It stays in the console, where it is still useful for support.
      const rawError = parsed?.errorText ?? 'Scan payload parsing failed'
      console.error('Barcode scan failed:', rawError)
      showScanAlert({ message: SCAN_ALERT_MESSAGES['scan-failed'] })
      notifyScanError(rawError)
    }
    notifyScanData(data)
  }
}

function cleanupGlobalZebraCallbacks() {
  if (typeof window === 'undefined') {
    return
  }
  delete window.onScanStartSuccess
  delete window.onScanStartFailure
  delete window.onScanStopSuccess
  delete window.onScanStopFailure
  delete window.receiveScanData
}

export function useZebraScanner(options?: UseZebraScannerOptions) {
  const onScanData = options?.onScanData
  const [androidBridgeAvailable, setAndroidBridgeAvailable] = useState(false)
  const [zebraDevice, setZebraDevice] = useState(false)
  const [lastScanData, setLastScanData] = useState<string | null>(null)
  const [scanEventCount, setScanEventCount] = useState(0)
  const [lastError, setLastError] = useState<string | null>(null)

  /* eslint-disable react-hooks-extra/no-direct-set-state-in-use-effect -- native bridge availability is resolved only after mount */
  useEffect(() => {
    // Resolve bridge/device info only after mount to avoid runtime bridge issues during render.
    // Fail-open behavior: if any native bridge call fails, hide scan controls instead of crashing.
    const bridgeAvailable = isAndroidBridgeAvailable()
    setAndroidBridgeAvailable(bridgeAvailable)

    if (!bridgeAvailable) {
      setZebraDevice(false)
      return
    }

    try {
      setZebraDevice(isZebraDevice())
    } catch {
      setZebraDevice(false)
    }
  }, [])
  /* eslint-enable react-hooks-extra/no-direct-set-state-in-use-effect */
  useEffect(() => {
    const handleScanData = (data: string) => {
      setLastScanData(data)
      setScanEventCount(c => c + 1)
      onScanData?.(data)
    }
    const handleScanError = (error: string | null) => {
      setLastError(error)
    }

    scanDataSubscribers.add(handleScanData)
    scanErrorSubscribers.add(handleScanError)
    callbackRegistrationCount += 1

    if (callbackRegistrationCount === 1) {
      ensureGlobalZebraCallbacks()
    }

    return () => {
      scanDataSubscribers.delete(handleScanData)
      scanErrorSubscribers.delete(handleScanError)
      callbackRegistrationCount = Math.max(0, callbackRegistrationCount - 1)
      if (callbackRegistrationCount === 0) {
        cleanupGlobalZebraCallbacks()
      }
    }
  }, [onScanData])

  const start = useCallback(() => {
    return startScanning(START_SUCCESS_CALLBACK, START_FAILURE_CALLBACK)
  }, [])

  const stop = useCallback(() => {
    return stopScanning(STOP_SUCCESS_CALLBACK, STOP_FAILURE_CALLBACK)
  }, [])

  const getScannedList = useCallback((orderId: string): ZebraScannedCrate[] => {
    const raw = getScanListForOrderIdAsJson(orderId)
    try {
      const parsed = JSON.parse(raw) as unknown
      if (!Array.isArray(parsed)) {
        setLastError(`Invalid scan list payload type for orderId "${orderId}"`)
        return []
      }

      return parsed.filter((item): item is ZebraScannedCrate => {
        if (typeof item !== 'object' || item === null) {
          return false
        }
        const maybe = item as Partial<ZebraScannedCrate>
        return typeof maybe.orderId === 'string'
          && typeof maybe.areaCode === 'string'
          && typeof maybe.crateNumber === 'string'
      })
    } catch {
      setLastError(`Invalid scan list payload for orderId "${orderId}"`)
      return []
    }
  }, [])

  return {
    isAndroidBridgeAvailable: androidBridgeAvailable,
    isZebraDevice: zebraDevice,
    start,
    stop,
    getScannedList,
    lastScanData,
    scanEventCount,
    lastError,
  }
}
